/**
 * Le quiz de prérequis, vu par la conversation de checkpoint (`useCheckpointConversation`) : une
 * `CheckpointApi` dont la réponse ne part pas au service IA (il refuse le quiz par un 422) mais
 * avance d'une question, et soumet la tentative à la dernière — l'ancien `SamConversationBase` en mode
 * quiz, sans seconde machine.
 *
 *  - chaque réponse est gardée avant la question suivante (`onProgress`) : un quiz interrompu reprend
 *    là où il s'était arrêté, comme avant ;
 *  - la dernière part avec toutes les autres (`submit`) ; le résultat devient le dernier tour, fini ;
 *  - une soumission en échec rejette : la conversation le montre, la progression d'avant reste, et le
 *    quiz rouvert repose la dernière question ;
 *  - le reste (l'avis, le pétale, la fermeture d'un quiz interrompu) part par les mêmes appels qu'un
 *    checkpoint (`base`), comme avant.
 */
import { type CheckpointApi } from "../../precall/checkpoint/checkpointApi"
import { type ApiCheckpointConversation } from "../../precall/checkpoint/checkpointConversationMachine"
import {
    plainAnswer,
    questionsOf,
    quizQuestionTurn,
    quizResultTurn,
    type ApiQuiz,
    type ApiQuizAttemptResponse,
    type QuizAnswer,
} from "./prerequisiteGate"

export type QuizCheckpointApiOptions = {
    quiz: ApiQuiz
    /** Où reprendre. */
    startIndex: number
    answers: QuizAnswer[]
    /** Les appels d'un checkpoint (l'avis, le pétale, la fermeture). */
    base: CheckpointApi
    /** La tentative : toutes les réponses, ensemble. */
    submit: (answers: QuizAnswer[]) => Promise<ApiQuizAttemptResponse>
    /** Une réponse donnée, avant la question suivante : la progression à garder. */
    onProgress: (answers: QuizAnswer[], nextIndex: number) => void
    /** La tentative est faite : la réponse du serveur, et le tour du résultat. */
    onAttempt: (response: ApiQuizAttemptResponse, result: ApiCheckpointConversation) => void
}

export function createQuizCheckpointApi(options: QuizCheckpointApiOptions): CheckpointApi {
    const { quiz, base, submit, onProgress, onAttempt } = options
    const questions = questionsOf(quiz)
    let index = options.startIndex
    let answers = options.answers

    return {
        ...base,
        respond: async (_conversationId, body) => {
            const question = questions[index]
            if (question == null) throw new Error("quiz: plus de question à répondre")
            const given = [
                ...answers,
                { quiz_question_id: question.id, answer: plainAnswer(question, body.student_response) },
            ]
            const next = index + 1
            if (next < questions.length) {
                answers = given
                index = next
                onProgress(given, next)
                return quizQuestionTurn(quiz, next, given.length)
            }
            const response = await submit(given)
            answers = given
            index = next
            const result = quizResultTurn(quiz, response)
            onAttempt(response, result)
            return result
        },
    }
}
